import React, { useEffect, useState, useRef } from "react";
import { Form, Input, Select, message, Tooltip, AutoComplete } from "antd";
import { makeRequest } from "../../api/api";
import { InfoCircleOutlined } from "@ant-design/icons";
import "../../css/freemium.css";

const { Option } = Select;

const FreemiumVesselCard = ({ onChange }) => {
  const [form] = Form.useForm();
  const [vessels, setVessels] = useState([]);
  const [imoOptions, setImoOptions] = useState([]);
  const [vesselNameOptions, setVesselNameOptions] = useState([]);
  const fetchingRef = useRef(false);

  useEffect(() => {
    form.setFieldsValue({
      pProp: 100,
      pWind: 0,
    });
    
    onChange?.({
      imoNumber: undefined,
      vesselName: undefined,
      vesselType: undefined,
      deadweight: undefined,
      pProp: 100,
      pWind: 0,
    });
  }, []);

  const fetchVesselSuggestions = async (searchValue) => {
    if (!searchValue || fetchingRef.current) return;

    fetchingRef.current = true;
    try {
      const res = await makeRequest(
        "get",
        `/v2/home/get_meta_info_for_vessel/?imo_number=${searchValue}`
      );

      const data = res?.data?.data || [];
      setVessels(data);
      
      // Create options for AutoComplete
      setImoOptions(data.map(v => ({ value: v.imo_number, label: v.imo_number })));
      setVesselNameOptions(data.map(v => ({ value: v.vessel_name, label: v.vessel_name })));
    } catch {
      message.error("Failed to fetch vessel list");
    } finally {
      fetchingRef.current = false;
    }
  };

  const autofillVessel = (vessel) => {
    if (!vessel) return;

    const mapped = {
      imoNumber: vessel.imo_number,
      vesselName: vessel.vessel_name,
      vesselType: vessel.vessel_type,
      deadweight: vessel.summer_dead_weight,
      pWind: vessel.pwind ?? 0,
      pProp: vessel.pprop ?? 100,
    };

    form.setFieldsValue(mapped);
    onChange?.(mapped);
  };

  const handleValuesChange = (_, allValues) => {
    onChange?.({
      imoNumber: allValues.imoNumber,
      vesselName: allValues.vesselName,
      vesselType: allValues.vesselType,
      deadweight: allValues.deadweight,
      pProp: allValues.pProp ?? 100,
      pWind: allValues.pWind ?? 0,
    });
  };

  return (
    <div className="freemium-right-cards">
      <h3 className="card-title">Vessel Information</h3>

      <Form
        form={form}
        layout="vertical"
        className="freemium-form"
        size="large"
        onValuesChange={handleValuesChange}
      >
        <div className="freemium-grid freemium-grid--voyage-single">

          <Form.Item label="IMO Number" name="imoNumber" >
            <AutoComplete
              
              options={imoOptions}
              onSearch={fetchVesselSuggestions}
              onSelect={(value) => {
                const vessel = vessels.find(v => v.imo_number === value);
                if (vessel) {
                  autofillVessel(vessel);
                }
              }}
              placeholder="eg.1234567"
              allowClear
            />
          </Form.Item>

          <Form.Item label="Vessel Name" name="vesselName">
            <AutoComplete
              options={vesselNameOptions}
              onSearch={fetchVesselSuggestions}
              onSelect={(value) => {
                const vessel = vessels.find(v => v.vessel_name === value);
                if (vessel) {
                  autofillVessel(vessel);
                }
              }}
              placeholder="Vessel Name"
              allowClear
            />
          </Form.Item>

          <Form.Item label="Deadweight" name="deadweight">
            <Input addonAfter="MT" placeholder="" />
          </Form.Item>

          <Form.Item label="Vessel Type" name="vesselType">
            <Select placeholder="Select vessel type">
              <Option value="Bulk Carrier">Bulk Carrier</Option>
              <Option value="Tanker">Tanker</Option>
              <Option value="Container">Container</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="pWind"
            label={
              <span>
                P-wind&nbsp;
                <Tooltip
                  title="Value to be 0 in case WAPS is not installed"
                  placement="top"
                  getPopupContainer={() => document.body}
                  overlayInnerStyle={{ whiteSpace: "normal", maxWidth: 260 }}
                >
                  <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
                </Tooltip>
              </span>
            }
          >
            <Input addonAfter="kW" placeholder="0" />
          </Form.Item>

          <Form.Item
            name="pProp"
            label={
              <span>
                P-prop&nbsp;
                <Tooltip
                  title="Value to be 100 in case WAPS is not installed"
                  placement="top"
                  getPopupContainer={() => document.body}
                  overlayInnerStyle={{ whiteSpace: "normal", maxWidth: 260 }}
                >
                  <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
                </Tooltip>
              </span>
            }
          >
            <Input addonAfter="kW" placeholder="100" />
          </Form.Item>

        </div>
      </Form>
    </div>
  );
};

export default FreemiumVesselCard;
